import React, {useContext, useState, useEffect, useCallback} from "react";
import "./StartupProjects.scss";
import {bigProjects} from "../../portfolio";
import {motion, AnimatePresence} from "framer-motion";
import StyleContext from "../../contexts/StyleContext";

export default function StartupProject() {
  const {isDark} = useContext(StyleContext);
  // State untuk modal proyek utama
  const [selectedProject, setSelectedProject] = useState(null);
  // State baru untuk index gambar di lightbox (null artinya lightbox tertutup)
  const [currentImageIndex, setCurrentImageIndex] = useState(null);

  function openUrlInNewTab(url) {
    if (!url || url === "#") return;
    window.open(url, "_blank").focus();
  }

  // Fungsi Navigasi Lightbox: Next
  const nextImage = useCallback(
    e => {
      if (e) e.stopPropagation();
      if (selectedProject?.gallery) {
        setCurrentImageIndex(prevIndex =>
          prevIndex === selectedProject.gallery.length - 1 ? 0 : prevIndex + 1
        );
      }
    },
    [selectedProject]
  );

  // Fungsi Navigasi Lightbox: Previous
  const prevImage = useCallback(
    e => {
      if (e) e.stopPropagation();
      if (selectedProject?.gallery) {
        setCurrentImageIndex(prevIndex =>
          prevIndex === 0 ? selectedProject.gallery.length - 1 : prevIndex - 1
        );
      }
    },
    [selectedProject]
  );

  // Handle Keyboard Navigation (Arrow Keys & Escape)
  useEffect(() => {
    if (currentImageIndex === null) return;

    const handleKeyDown = e => {
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") setCurrentImageIndex(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentImageIndex, nextImage, prevImage]);

  if (!bigProjects.display) {
    return null;
  }

  return (
    <div className="main" id="projects">
      {/* --- MAIN PROJECT LIST --- */}
      <motion.div
        initial={{y: 50, opacity: 0}}
        whileInView={{y: 0, opacity: 1}}
        transition={{duration: 0.5}}
        viewport={{once: true}}
      >
        <h1 className="skills-heading">{bigProjects.title}</h1>
        <p
          className={
            isDark ? "dark-mode project-subtitle" : "subTitle project-subtitle"
          }
        >
          {bigProjects.subtitle}
        </p>

        <div className="projects-section-container">
          {bigProjects.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="project-category-section">
              <h2
                className={isDark ? "dark-mode section-title" : "section-title"}
                style={{
                  textAlign: "left",
                  marginTop: "40px",
                  marginBottom: "20px",
                  paddingLeft: "10px",
                  borderLeft: "5px solid #6c63ff",
                  fontSize: "1.5rem"
                }}
              >
                {section.title}
              </h2>

              <div className="projects-container">
                {section.projects.map((project, i) => (
                  <div
                    key={i}
                    className={
                      isDark
                        ? "dark-mode project-card project-card-dark"
                        : "project-card project-card-light"
                    }
                    onClick={() => setSelectedProject(project)}
                    style={{cursor: "pointer"}}
                  >
                    {project.image && (
                      <div className="project-image">
                        <img
                          src={project.image}
                          alt={project.projectName}
                          className="card-image"
                        />
                      </div>
                    )}
                    <div className="project-detail">
                      <h5
                        className={
                          isDark ? "dark-mode card-title" : "card-title"
                        }
                      >
                        {project.projectName}
                      </h5>
                      <p
                        className={
                          isDark ? "dark-mode card-subtitle" : "card-subtitle"
                        }
                      >
                        {project.projectDesc}
                      </p>
                      <span
                        className="learn-more-btn"
                        style={{
                          color: isDark ? "#fff" : "#000",
                          fontSize: "0.9em",
                          fontWeight: "bold",
                          marginTop: "10px",
                          display: "block"
                        }}
                      >
                        View Gallery & Details <i className="fas fa-images"></i>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* --- MODAL DETAIL PROJECT --- */}
      <AnimatePresence>
        {selectedProject && (
          <div
            className="modal-overlay"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className={`modal-content ${isDark ? "dark-modal" : "light-modal"}`}
              initial={{scale: 0.8, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              exit={{scale: 0.8, opacity: 0}}
              onClick={e => e.stopPropagation()}
            >
              <button
                className="close-btn"
                onClick={() => setSelectedProject(null)}
              >
                &times;
              </button>

              <h2 className="modal-title">{selectedProject.projectName}</h2>

              {/* GALLERY THUMBNAILS */}
              {selectedProject.gallery &&
                selectedProject.gallery.length > 0 && (
                  <div className="gallery-section">
                    <h4 className="gallery-title">
                      📸 Project Gallery (Click to Zoom)
                    </h4>
                    <div className="gallery-grid">
                      {selectedProject.gallery.map((img, idx) => (
                        // UBAH DISINI: Klik thumbnail membuka Lightbox
                        <div
                          key={idx}
                          className="gallery-item"
                          onClick={() => setCurrentImageIndex(idx)}
                        >
                          <img
                            src={img}
                            alt={`Screenshot ${idx + 1}`}
                            loading="lazy"
                          />
                          <div className="gallery-overlay">
                            <i className="fas fa-search-plus"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* CASE STUDY CONTENT (Sama seperti sebelumnya) */}
              {selectedProject.caseStudy ? (
                <div className="case-study-content">
                  <div className="cs-section">
                    <h4>🎯 The Challenge</h4>
                    <p>{selectedProject.caseStudy.challenge}</p>
                  </div>
                  <div className="cs-section">
                    <h4>💡 The Solution & Architecture</h4>
                    <p>{selectedProject.caseStudy.solution}</p>
                    <div className="tech-tags">
                      {selectedProject.caseStudy.architecture.map((tech, k) => (
                        <span key={k} className="tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="cs-section">
                    <h4>🚀 Impact</h4>
                    <p>{selectedProject.caseStudy.impact}</p>
                  </div>
                </div>
              ) : (
                <p className="no-detail">Detail studi kasus belum tersedia.</p>
              )}

              {selectedProject.footerLink && (
                <div className="modal-footer">
                  {selectedProject.footerLink.map((link, i) => (
                    <button
                      key={i}
                      className="action-btn"
                      onClick={() => openUrlInNewTab(link.url)}
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- NEW: LIGHTBOX FULLSCREEN GALLERY --- */}
      <AnimatePresence>
        {currentImageIndex !== null && selectedProject?.gallery && (
          <motion.div
            className="lightbox-overlay"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            onClick={() => setCurrentImageIndex(null)} // Klik background untuk tutup
          >
            {/* Tombol Close Lightbox */}
            <button
              className="lightbox-close-btn"
              onClick={() => setCurrentImageIndex(null)}
            >
              <i className="fas fa-times"></i>
            </button>

            {/* Tombol Prev (Hanya muncul jika ada lebih dari 1 gambar) */}
            {selectedProject.gallery.length > 1 && (
              <button className="lightbox-nav-btn prev-btn" onClick={prevImage}>
                <i className="fas fa-chevron-left"></i>
              </button>
            )}

            {/* Wadah Gambar */}
            <motion.div
              className="lightbox-image-container"
              initial={{scale: 0.8, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              onClick={e => e.stopPropagation()} // Mencegah tutup saat klik gambar
            >
              {/* Menggunakan AnimatePresence lagi untuk transisi antar gambar */}
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex} // Key penting untuk animasi antar gambar
                  src={selectedProject.gallery[currentImageIndex]}
                  alt={`Gallery full ${currentImageIndex}`}
                  initial={{opacity: 0, x: 50}}
                  animate={{opacity: 1, x: 0}}
                  exit={{opacity: 0, x: -50}}
                  transition={{duration: 0.3}}
                />
              </AnimatePresence>

              {/* Counter Gambar */}
              <div className="lightbox-counter">
                {currentImageIndex + 1} / {selectedProject.gallery.length}
              </div>
            </motion.div>

            {/* Tombol Next */}
            {selectedProject.gallery.length > 1 && (
              <button className="lightbox-nav-btn next-btn" onClick={nextImage}>
                <i className="fas fa-chevron-right"></i>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
